import React from 'react';
import {
  Button,
  ButtonProps,
  IconButton,
} from '@dropbox/dig-components/buttons';
import {
  useSignatureRequestContext,
  useFieldsContext,
} from 'signer-app/signature-request/context';
import {
  useSignerAppParams,
  useSignerContext,
} from 'signer-app/signer-experience/signer-experience';
import { FormattedMessage } from 'react-intl';
import styles from 'signer-app/signer-experience/next-button.module.css';
import { useHistory } from 'react-router';
import { useIsMutating } from '@tanstack/react-query';
import { useTouchedFieldIds } from 'signer-app/signer-experience/use-validation';
import * as Sentry from '@sentry/browser';
import invariant from 'invariant';
import classNames from 'classnames';
import { VisuallyHidden } from '@dropbox/dig-foundations';
import { useAttachmentsInfo } from 'signer-app/utils/use-attachments-info';
import { useAtomValue, useSetAtom } from 'jotai';
import { isAttachmentsDrawerOpen } from 'signer-app/parts/attachments-drawer/attachments-drawer';
import { useShouldShowDrawer } from 'signer-app/utils/use-should-show-drawer';
import { accessibleViewAtom } from 'signer-app/signer-signature-document/accessible-document-toggle';
import { useFeatureFlag } from 'signer-app/context/feature-flags';

const ButtonMessage = ({
  step,
  requiredFieldsLeft,
  allValid,
  isFormView,
  hasAttachments,
  requiredAttachmentsLeft,
}: {
  step: string;
  requiredFieldsLeft: number;
  allValid: boolean;
  isFormView: boolean;
  hasAttachments: boolean;
  requiredAttachmentsLeft: number;
}) => {
  switch (step) {
    case 'preview':
      return (
        <FormattedMessage
          id="signer.preview.getStartedButton.labelText"
          defaultMessage="Get started"
          description="Button text in signer flow, when clicked goes to next page to fill input fields and sign"
        />
      );
    case 'sign': {
      if (
        isFormView ||
        ((!hasAttachments || requiredAttachmentsLeft === 0) &&
          requiredFieldsLeft === 0 &&
          allValid)
      ) {
        return (
          <FormattedMessage
            id="88555b111c0c2bcc4ac26c6cac92b51bc4ac747dff398cf96777fa2b10e11c48"
            defaultMessage="Continue"
            description="Next button in the signer experience."
          />
        );
      }

      return (
        <FormattedMessage
          id="e68dfb13e9194b0ab2b9282009d009eb9089ad52d6ce994f0f12fba939b0d050"
          defaultMessage="Next"
          description="Next button in the signer experience."
        />
      );
    }
    case 'attachments':
      if (
        (requiredAttachmentsLeft === 0 &&
          requiredFieldsLeft === 0 &&
          allValid) ||
        isFormView
      ) {
        return (
          <FormattedMessage
            id="88555b111c0c2bcc4ac26c6cac92b51bc4ac747dff398cf96777fa2b10e11c48"
            defaultMessage="Continue"
            description="Next button in the signer experience."
          />
        );
      }

      return (
        <FormattedMessage
          id="e68dfb13e9194b0ab2b9282009d009eb9089ad52d6ce994f0f12fba939b0d050"
          defaultMessage="Next"
          description="Next button in the signer experience."
        />
      );
    case 'review':
      return (
        <FormattedMessage
          id="baa002335fe313938d34e2258f01d304e8efa2b6527201dd981b148922a8ec94"
          defaultMessage="I Agree"
          description="This button completes the signer experience"
        />
      );
    default:
      return null;
  }
};

export const NextButton: React.FC<{
  fullWidth?: boolean;
  size?: ButtonProps['size'];
  children?: React.ReactNode;
  icon?: boolean;
  className?: string;
}> = ({ fullWidth, size, children, icon = false, className }) => {
  const { selectedFieldIds } = useSignatureRequestContext();
  const { step, signatureRequestId } = useSignerAppParams();
  const {
    selectField,
    requiredFieldsLeft,
    attachmentSlots,
    startEnabled,
    validationErrors,
    finalize,
    flushDraft,
    isQualifiedSignature,
    allValid,
  } = useSignerContext();

  const { requiredAttachmentsLeft } = useAttachmentsInfo();
  const isMutating = useIsMutating() > 0;
  const fixFinalizeRace = useFeatureFlag(
    'sign_core_2026_03_02_fix_finalize_saving_race',
  );
  const fixInvariant = useFeatureFlag(
    'sign_core_2026_03_03_fix_group_requirement_invariant',
  );

  const setIsAttachmentsDrawerOpen = useSetAtom(isAttachmentsDrawerOpen);
  const isFormView = useAtomValue(accessibleViewAtom);
  const isUsingDrawer = useShouldShowDrawer();

  const [activeField] = selectedFieldIds;
  const fields = useFieldsContext();
  const history = useHistory();

  const touchedFieldIds = useTouchedFieldIds();
  const touchedAllFields = React.useMemo(() => {
    return fields.every((f) => touchedFieldIds.has(f.id));
  }, [fields, touchedFieldIds]);
  const nextRequired = requiredFieldsLeft > 0 && touchedAllFields && !allValid;

  // Helper functions to reduce code duplication
  const getBasePath = () => `/sign/${signatureRequestId}`;

  const getAttachmentsPath = () => `${getBasePath()}/attachments/`;

  const getQesOrReviewPath = (currentStep: string = step) => {
    return isQualifiedSignature
      ? `${getBasePath()}/${currentStep}?qes`
      : `${getBasePath()}/review/`;
  };

  const navigateToQesOrReview = (currentStep: string = step) => {
    if (fixFinalizeRace) flushDraft();
    history.push(getQesOrReviewPath(currentStep));
  };

  const navigateToAttachments = () => {
    history.push(getAttachmentsPath());
  };

  const hasAttachments = attachmentSlots.length > 0;

  const handleAttachmentsStep = () => {
    if (requiredAttachmentsLeft > 0) {
      setIsAttachmentsDrawerOpen(true);
    } else {
      navigateToQesOrReview();
    }
  };

  const handleFieldsFilled = () => {
    if (hasAttachments) {
      navigateToAttachments();
      if (!isUsingDrawer) {
        setIsAttachmentsDrawerOpen(true);
      }
      return;
    }

    navigateToQesOrReview();
  };

  const handleFieldsUnfilled = () => {
    if (nextRequired) {
      const i = fields.findIndex((f) => f.id === activeField);
      const field = fields
        .concat(fields)
        .slice(i + 1)
        .find((f) => f.required && validationErrors[f.id]);

      if (field) {
        selectField(field.id);
        return;
      }

      if (!fixInvariant) {
        invariant(field, 'Field not found');
      }
      // When fixInvariant is on: requiredFieldsLeft > 0 but no field has a
      // validation error — fall through to default field cycling.
      Sentry.captureMessage(
        'handleFieldsUnfilled: nextRequired is true but no field with validation error found',
      );
    }

    if (!activeField) {
      const field = fields[0];
      selectField(field.id);
    } else {
      const i = fields.findIndex((f) => f.id === activeField);
      const field = fields[(i + 1) % fields.length];
      selectField(field.id);
    }
  };

  /**
   * Every branch except the final review should navigate the user to a new
   * location.  Even the branches that are selecting a field, will change the
   * URL to match the field.
   */
  const handleNext = () => {
    if (step === 'review') {
      finalize();
      return;
    }

    if (step === 'attachments') {
      handleAttachmentsStep();
      return;
    }

    if (step === 'sign' && requiredFieldsLeft === 0 && allValid) {
      handleFieldsFilled();
      return;
    }

    handleFieldsUnfilled();
  };

  // If we're on the review step we should wait for any mutations to resolve
  // before allowing the user to click 'I Agree'
  const awaitingFinalSave = step === 'review' && isMutating;

  const disabled = React.useMemo(() => {
    if (!startEnabled) return true;

    if (awaitingFinalSave) return true;

    // Form view can be displayed on both sign and attachments steps
    if (isFormView) {
      return !(
        requiredFieldsLeft === 0 &&
        allValid &&
        requiredAttachmentsLeft === 0
      );
    }

    return false;
  }, [
    isFormView,
    requiredAttachmentsLeft,
    requiredFieldsLeft,
    allValid,
    startEnabled,
    awaitingFinalSave,
  ]);

  if (icon) {
    return (
      <>
        <IconButton
          className={classNames(className, styles.small)}
          variant={'opacity'}
          disabled={disabled}
          size={size}
          onClick={handleNext}
          aria-labelledby="next-button-label"
          data-testid="button-next"
        >
          {children}
        </IconButton>
        <div id="next-button-label">
          <VisuallyHidden>
            <ButtonMessage
              hasAttachments={hasAttachments}
              step={step}
              requiredFieldsLeft={requiredFieldsLeft}
              allValid={allValid}
              isFormView={isFormView}
              requiredAttachmentsLeft={requiredAttachmentsLeft}
            />
          </VisuallyHidden>
        </div>
      </>
    );
  }

  return (
    <Button
      className={className}
      variant={'primary'}
      fullWidth={fullWidth}
      size={size}
      disabled={disabled}
      onClick={handleNext}
      data-testid="button-next"
    >
      <ButtonMessage
        step={step}
        hasAttachments={hasAttachments}
        requiredFieldsLeft={requiredFieldsLeft}
        allValid={allValid}
        isFormView={isFormView}
        requiredAttachmentsLeft={requiredAttachmentsLeft}
      />
    </Button>
  );
};
